// Brings the Bloomreach collections widget back to life after a bfcache
// restore without reloading the page (SM-685, SM-773).
//
// This theme's cross-document view transitions mean popstate never fires for
// a back-navigation from a different document (e.g. a PDP) — see
// assets/scroll-container.js. Only pageshow does.
//
// The Loomi collections widget tears itself down on `pagehide` (vendor
// bundle: `addEventListener("pagehide", () => destroyAll())`), removing its
// br-facet-change / br-sort-change / br-page-change / br-facet-clear-all and
// popstate handlers, and never rebuilds them on `pageshow`. The restored page
// looks right but filtering, sorting and paging silently no-op (SM-685).
//
// SM-685 fixed that with a full reload, which made the PLP visibly flicker
// and reload on every back-navigation (SM-773). Re-initialising eagerly on
// pageshow is no better: the widget's first render paints a loading skeleton
// over the restored grid. So the restored page is left untouched, and the
// widget is re-initialised on the user's first interaction with it:
// `initBloomreachCollectionsWidget(id)` attaches its handlers synchronously,
// and the intercepted event is re-dispatched to them. The user then sees the
// same loading state as any other filter/sort change.
//
// If the vendor global is ever missing, fall back to the SM-685 reload.

const RESULTS_PREFIX = 'br-collections-results-';
const WIDGET_EVENTS = ['br-facet-change', 'br-sort-change', 'br-page-change', 'br-facet-clear-all'];

/** @type {AbortController | null} */
let armed = null;

/**
 * @param {HTMLElement} container The widget's results container, where its handlers live.
 * @param {AbortController} controller Disarms every listener armed for this restore.
 */
function armLazyReinit(container, controller) {
  const widgetId = container.id.slice(RESULTS_PREFIX.length);
  const { signal } = controller;

  const reinit = () => {
    controller.abort();
    window.initBloomreachCollectionsWidget(widgetId);
  };

  /** @param {Event} event */
  const onWidgetEvent = (event) => {
    event.stopImmediatePropagation();
    reinit();
    const detail = event instanceof CustomEvent ? event.detail : undefined;
    container.dispatchEvent(new CustomEvent(event.type, { detail, bubbles: true, composed: true }));
  };

  for (const type of WIDGET_EVENTS) {
    container.addEventListener(type, onWidgetEvent, { capture: true, signal });
  }
  // Same-document history entries (applied filters) still fire popstate;
  // a fresh init reads the state back from the URL, as the widget's own
  // popstate handler would.
  window.addEventListener('popstate', reinit, { signal });
}

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;

  armed?.abort();
  armed = null;

  /** @type {NodeListOf<HTMLElement>} */
  const containers = document.querySelectorAll(`.br-widget-host--collections [id^="${RESULTS_PREFIX}"]`);
  if (!containers.length) return;

  if (typeof window.initBloomreachCollectionsWidget !== 'function') {
    window.location.reload();
    return;
  }

  const controller = new AbortController();
  armed = controller;
  for (const container of containers) armLazyReinit(container, controller);
});
